import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Calendar,
  Check,
  Users,
  ShoppingCart,
  Mail,
  Building2,
  Briefcase,
  Globe,
  MessageSquare,
  DollarSign,
  CheckCircle2,
  ArrowRight,
  Zap,
} from "lucide-react";
import { toast } from "react-toastify";

import Navbar from "../Navbar/Navbar";
import Footer from "../LandingPage/Footer";

import "./Demo.css";

const DEFAULT_API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const FEATURES = [
  {
    icon: MessageSquare,
    title: "Intelligent customer support",
    description: "AI-powered responses that learn from your data",
  },
  {
    icon: Users,
    title: "Enhanced employee experience",
    description: "Streamline HR and internal operations",
  },
  {
    icon: ShoppingCart,
    title: "AI-powered product recommendations",
    description: "Boost sales with personalized suggestions",
  },
  {
    icon: Zap,
    title: "Reduce queries by 70%",
    description: "Automate repetitive questions instantly",
  },
];

const STATS = [
  { number: "70%", label: "Reduction in queries" },
  { number: "24/7", label: "Available support" },
  { number: "50+", label: "Integrations" },
];

export default function Demo({ apiBaseUrl, onSuccess }) {
  const baseUrl = useMemo(() => apiBaseUrl || DEFAULT_API_BASE_URL, [apiBaseUrl]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    jobTitle: "",
    country: "",
    monthlyQueries: "",
    budget: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  // Prefill from localStorage user (if present)
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return;

    try {
      const user = JSON.parse(storedUser);
      const fullName = (user?.name || "").trim();
      const [first = "", last = ""] = fullName.split(" ");

      setFormData((prev) => ({
        ...prev,
        firstName: first || prev.firstName,
        lastName: last || prev.lastName,
        email: user?.email || prev.email,
      }));
    } catch (e) {
      // ignore parse errors
      console.error("Error parsing user from localStorage:", e);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      jobTitle: "",
      country: "",
      monthlyQueries: "",
      budget: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!baseUrl) {
      setError("API base URL is not configured (REACT_APP_BACKEND_API_URL missing).");
      toast.error("API base URL missing in env.");
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(`${baseUrl}/api/auth/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        // If backend doesn't return JSON
        data = {};
      }

      if (res.ok) {
        setIsSuccess(true);
        toast.success("🎉 Demo scheduled successfully!");
        onSuccess?.();

        // optional reset after 3s
        setTimeout(() => {
          resetForm();
          setIsSuccess(false);
        }, 3000);
      } else {
        const msg = data?.message || "Failed to schedule demo. Please try again.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error("Demo submit error:", err);
      const msg = "Server error. Please try again later.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modern-demo-shell">
      <Navbar />

      <div className="modern-demo-page">
        {/* Hero Section with Form */}
        <section className="demo-hero-section">
          <div className="demo-hero-container">
            <div className="demo-hero-grid">
              {/* Left: Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                className="demo-content"
              >
                {/* Avatar Group */}
                

                <h1 className="demo-hero-title">
                  Automate Your Business
                  <br />
                  <span className="title-gradient">with XenaChat AI Agents</span>
                </h1>

                <p className="demo-hero-description">
                  Unlock the power of AI to transform your e-commerce, customer service, and HR
                  operations with intelligent automation.
                </p>

                <div className="demo-features">
                  {FEATURES.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="demo-feature-item"
                    >
                      <div className="feature-icon-wrapper">
                        <feature.icon className="feature-icon" />
                      </div>
                      <div className="feature-content">
                        <div className="feature-title">{feature.title}</div>
                        <div className="feature-description">{feature.description}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="demo-stats">
                  {STATS.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      className="demo-stat"
                    >
                      <div className="stat-number">{stat.number}</div>
                      <div className="stat-label">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right: Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="demo-form-wrapper"
              >
                <div className="demo-form-card">
                  <div className="form-header">
                    <div className="form-icon">
                      <Calendar className="icon" />
                    </div>
                    <h2 className="form-title">Schedule Your Demo</h2>
                    <p className="form-subtitle">Get a personalized, no-commitment consultation</p>
                  </div>

                  <AnimatePresence mode="wait">
                    {isSuccess ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="success-message"
                      >
                        <div className="success-icon">
                          <CheckCircle2 className="icon" />
                        </div>
                        <h3 className="success-title">Demo Scheduled! 🎉</h3>
                        <p className="success-text">
                          We'll be in touch shortly to confirm your demo session.
                        </p>
                      </motion.div>
                    ) : (
                      <motion.form
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onSubmit={handleSubmit}
                        className="demo-form"
                      >
                        {/* Name Row */}
                        <div className="form-row">
                          <div className="form-field">
                            <label className="form-label">First name</label>
                            <input
                              type="text"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                              placeholder="John"
                              required
                              className="form-input"
                            />
                          </div>
                          <div className="form-field">
                            <label className="form-label">Last name</label>
                            <input
                              type="text"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                              placeholder="Doe"
                              required
                              className="form-input"
                            />
                          </div>
                        </div>

                        {/* Email & Company Row */}
                        <div className="form-row">
                          <div className="form-field">
                            <label className="form-label">Work email</label>
                            <div className="input-with-icon">
                              <Mail className="input-icon" />
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="john@company.com"
                                required
                                className="form-input has-icon"
                              />
                            </div>
                          </div>
                          <div className="form-field">
                            <label className="form-label">Company name</label>
                            <div className="input-with-icon">
                              <Building2 className="input-icon" />
                              <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                placeholder="Acme Inc"
                                required
                                className="form-input has-icon"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Job Title & Country Row */}
                        <div className="form-row">
                          <div className="form-field">
                            <label className="form-label">Job title</label>
                            <div className="input-with-icon">
                              <Briefcase className="input-icon" />
                              <input
                                type="text"
                                name="jobTitle"
                                value={formData.jobTitle}
                                onChange={handleInputChange}
                                placeholder="CEO"
                                required
                                className="form-input has-icon"
                              />
                            </div>
                          </div>
                          <div className="form-field">
                            <label className="form-label">Country/Region</label>
                            <div className="input-with-icon">
                              <Globe className="input-icon" />
                              <input
                                type="text"
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                placeholder="India"
                                required
                                className="form-input has-icon"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Monthly Queries */}
                        <div className="form-field full-width">
                          <label className="form-label">Monthly queries</label>
                          <div className="input-with-icon">
                            <MessageSquare className="input-icon" />
                            <select
                              name="monthlyQueries"
                              value={formData.monthlyQueries}
                              onChange={handleInputChange}
                              required
                              className="form-select has-icon"
                            >
                              <option value="">Select query volume</option>
                              <option value="0-100">0-100</option>
                              <option value="100-1000">100-1,000</option>
                              <option value="1000-5000">1,000-5,000</option>
                              <option value="5000+">5,000+</option>
                            </select>
                          </div>
                        </div>

                        {/* Budget */}
                        <div className="form-field full-width">
                          <label className="form-label">Budget</label>
                          <div className="input-with-icon">
                            <DollarSign className="input-icon" />
                            <select
                              name="budget"
                              value={formData.budget}
                              onChange={handleInputChange}
                              required
                              className="form-select has-icon"
                            >
                              <option value="">Select budget range</option>
                              <option value="low">Less than $1,000</option>
                              <option value="medium">$1,000 - $10,000</option>
                              <option value="high">$10,000 - $50,000</option>
                              <option value="enterprise">$50,000+</option>
                            </select>
                          </div>
                        </div>

                        {error && (
                          <div className="error-message">
                            <span>{error}</span>
                          </div>
                        )}

                        <button type="submit" disabled={isSubmitting} className="form-submit-btn">
                          {isSubmitting ? (
                            <>
                              <div className="btn-spinner"></div>
                              <span>Scheduling...</span>
                            </>
                          ) : (
                            <>
                              <Calendar className="btn-icon" />
                              <span>Schedule Demo</span>
                              <ArrowRight className="btn-icon" />
                            </>
                          )}
                        </button>

                        <p className="form-note">
                          By submitting, you agree to our terms and privacy policy.
                        </p>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section className="demo-support-section">
          <div className="demo-support-container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="support-card"
            >
              <div className="support-content">
                <div className="support-icon-group">
                  <div className="support-avatar-stack">
                    <div className="support-avatar">👨‍💼</div>
                    <div className="support-avatar">👩‍💼</div>
                    <div className="support-avatar">👨‍💻</div>
                  </div>
                </div>

                <div className="support-text">
                  <h3 className="support-title">Looking for support?</h3>
                  <p className="support-description">
                    We're here to help! Get in touch if you have technical, account, or billing
                    questions.
                  </p>
                </div>

                <a
                  href="mailto:support@xenachat.com?subject=Support%20Request&body=Hi%20Support%20Team,%0D%0A%0D%0AI%20need%20help%20with..."
                  className="support-btn"
                >
                  <Mail className="btn-icon" />
                  <span>Contact Support</span>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

      
      </div>

      <Footer />
    </div>
  );
}
